import { create } from 'zustand';
import { UI_SHARE_KEY_STATUS } from '~src/types/db.d.ts';

export enum DownloadingState {
  PREPARING = 'PREPARING',
  FETCHING = 'FETCHING',
  DOWNLOADING = 'DOWNLOADING',
  FINISHING = 'FINISHING',
}

type Store = {
  filterStates: UI_SHARE_KEY_STATUS[];
  addToFilter: (filter: number) => void;
  removeFromFilter: (filter: number) => void;
  setAllSelected: () => void;
  removeAllSelected: () => void;

  // Primary-/Secondary Share Keys: admin Shares-tab filter - "all share
  // keys" vs "only primary vendors that have invited secondary keys"
  // (has_secondary_share_keys === true). Orthogonal to filterStates above.
  shareKeysInviteFilter: 'all' | 'withSecondaryInvites';
  setShareKeysInviteFilter: (value: 'all' | 'withSecondaryInvites') => void;

  // Tender registrations filter
  tenderFilterStates: number[];
  addToTenderFilter: (filter: number) => void;
  removeFromTenderFilter: (filter: number) => void;
  setAllTenderSelected: () => void;
  removeAllTenderSelected: () => void;
  // Tender registrations deleted toggle
  tenderShowDeleted: boolean;
  setTenderShowDeleted: (state: boolean) => void;
  // "Deleted" state checkbox in popup depends on tenderShowDeleted
  tenderIncludeDeletedState: boolean;
  setTenderIncludeDeletedState: (state: boolean) => void;

  // Module registrations filter
  moduleFilterStates: number[];
  addToModuleFilter: (filter: number) => void;
  removeFromModuleFilter: (filter: number) => void;
  setAllModulesSelected: () => void;
  removeAllModulesSelected: () => void;

  // Registration file filter
  registrationFileFilter: 'all' | 'files-only';
  setRegistrationFileFilter: (filter: 'all' | 'files-only') => void;

  downloadingState: keyof typeof DownloadingState;
  isDownloadingActive: boolean;
  isOfferDownload: boolean;
  changeDownloadingState: (state: keyof typeof DownloadingState) => void;
  changeDownloadingActive: (state: boolean) => void;
  setIsOfferDownload: (state: boolean) => void;
  totalDownloadFolders: (state: number) => void;
  totalDownloadFiles: (state: number) => void;
  totalDownloadSize: (state: number) => void;
  currentDownloadFile: (state: number) => void;
  totalFoldersNumber: number;
  totalFilesNumber: number;
  totalSizeDownload: number;
  totalFileSize: number;
  changeTotalFileSize: (state: number) => void;
  currentFileDownloaded: number;
  filesToBigChecker: boolean;
  changeFilesToBigChecker: (state: boolean) => void;
  errorList: string[];
  addError: (error: string) => void;
  clearErrors: () => void;

  isUploadingActive: boolean;
  changeUploadingActive: (state: boolean) => void;
};

export const useStore = create<Store>((set) => ({
  filterStates: [
    UI_SHARE_KEY_STATUS.AWAITING,
    UI_SHARE_KEY_STATUS.INVITED,
    UI_SHARE_KEY_STATUS.OPENED,
    UI_SHARE_KEY_STATUS.CLAIMED,
    UI_SHARE_KEY_STATUS.REJECTED,
    UI_SHARE_KEY_STATUS.DELETED,
  ],

  // Tender registrations filter | JIRA: TV1-332
  tenderFilterStates: [1, 3, 6, 10],
  addToTenderFilter: (filter: number) =>
    set((state) => ({
      tenderFilterStates: [...state.tenderFilterStates, filter],
    })),
  removeFromTenderFilter: (filter: number) =>
    set((state) => ({
      tenderFilterStates: state.tenderFilterStates.filter((v) => v !== filter),
    })),
  setAllTenderSelected: () =>
    set(() => ({
      tenderFilterStates: [1, 3, 6, 10],
    })),
  removeAllTenderSelected: () =>
    set(() => ({
      tenderFilterStates: [],
    })),

  // Tender registrations deleted toggle
  tenderShowDeleted: false,
  setTenderShowDeleted: (tenderShowDeleted: boolean) =>
    set(() => ({
      tenderShowDeleted,
      // if we stop requesting deleted, also disable deleted-state visibility
      tenderIncludeDeletedState: tenderShowDeleted ? true : false,
    })),

  // Deleted state checkbox inside filter
  tenderIncludeDeletedState: false,
  setTenderIncludeDeletedState: (tenderIncludeDeletedState: boolean) =>
    set(() => ({
      tenderIncludeDeletedState,
    })),

  // Module registrations filter
  moduleFilterStates: [1, 3, 4, 5, 6, 7, 8, 9], // All modules selected by default
  addToModuleFilter: (filter: number) =>
    set((state) => ({
      moduleFilterStates: [...state.moduleFilterStates, filter],
    })),
  removeFromModuleFilter: (filter: number) =>
    set((state) => ({
      moduleFilterStates: state.moduleFilterStates.filter((v) => v !== filter),
    })),
  setAllModulesSelected: () =>
    set(() => ({
      moduleFilterStates: [1, 3, 4, 5, 6, 7, 8, 9],
    })),
  removeAllModulesSelected: () =>
    set(() => ({
      moduleFilterStates: [],
    })),

  registrationFileFilter: 'all',
  setRegistrationFileFilter: (filter: 'all' | 'files-only') =>
    set(() => ({
      registrationFileFilter: filter,
    })),

  isDownloadingActive: false,
  isOfferDownload: false,
  downloadingState: 'PREPARING',
  totalFoldersNumber: 0,
  totalFilesNumber: 0,
  totalSizeDownload: 0,
  currentFileDownloaded: 0,
  totalFileSize: 0,
  filesToBigChecker: true,
  errorList: [],
  isUploadingActive: false,
  addError: (error: string) =>
    set((state) => ({
      errorList: [...state.errorList, error],
    })),
  // 🆕 Clear all errors
  clearErrors: () =>
    set(() => ({
      errorList: [],
    })),
  changeDownloadingState: (downloadingState) => {
    if (downloadingState === DownloadingState.FINISHING) {
      set({
        downloadingState,
        totalFoldersNumber: 0,
        totalFilesNumber: 0,
        totalSizeDownload: 0,
        currentFileDownloaded: 1,
      });
    } else {
      set({ downloadingState });
    }
  },
  changeFilesToBigChecker: (filesToBigChecker: boolean) =>
    set((state) => ({
      filesToBigChecker,
    })),
  changeTotalFileSize: (totalFileSize: number) =>
    set((state) => ({
      totalFileSize,
    })),
  changeDownloadingActive: (isDownloadingActive: boolean) =>
    set((state) => ({
      isDownloadingActive,
    })),
  setIsOfferDownload: (isOfferDownload: boolean) =>
    set(() => ({
      isOfferDownload,
    })),
  totalDownloadFolders: (totalFoldersNumber: number) =>
    set((state) => ({
      totalFoldersNumber,
    })),
  totalDownloadFiles: (totalFilesNumber: number) =>
    set((state) => ({
      totalFilesNumber,
    })),
  currentDownloadFile: (currentFileDownloaded: number) =>
    set((state) => ({
      currentFileDownloaded,
    })),
  totalDownloadSize: (totalSizeDownload: number) =>
    set((state) => ({
      totalSizeDownload,
    })),
  addToFilter: (filter: UI_SHARE_KEY_STATUS) =>
    set((state) => ({
      filterStates: [...state.filterStates, filter],
    })),
  removeFromFilter: (filter: UI_SHARE_KEY_STATUS) =>
    set((state) => ({
      filterStates: state.filterStates.filter((state) => state !== filter),
    })),
  setAllSelected: () =>
    set(() => ({
      filterStates: [
        UI_SHARE_KEY_STATUS.AWAITING,
        UI_SHARE_KEY_STATUS.INVITED,
        UI_SHARE_KEY_STATUS.OPENED,
        UI_SHARE_KEY_STATUS.CLAIMED,
        UI_SHARE_KEY_STATUS.REJECTED,
        UI_SHARE_KEY_STATUS.DELETED,
      ],
    })),
  removeAllSelected: () =>
    set(() => ({
      filterStates: [],
    })),

  shareKeysInviteFilter: 'all',
  setShareKeysInviteFilter: (value: 'all' | 'withSecondaryInvites') =>
    set(() => ({
      shareKeysInviteFilter: value,
    })),
  changeUploadingActive: (isUploadingActive: boolean) =>
    set(() => ({
      isUploadingActive,
    })),
}));
